import { Box, Center, Stack, Text, Icon } from '@chakra-ui/react';
import { find, get } from 'lodash';
import type { Session, OriginalSession } from '@halo/chat-sdk-react';
import {
  isSessionCategory,
  SessionCategoryPrefix,
  useAppStore,
  getSessionUnreadCount,
  AccountType,
  StateEnum,
} from '@halo/chat-sdk-react';
import { useShallowEnhance } from '@halo/zustand-enhance';
import { useTranslation } from 'react-i18next';
import { Avatar, ContextMenuTrigger, UnreadBadge } from '../';
import { GroupIcon, MentionIcon, RobotIcon } from '../../assets/icons';

interface PinnedItemProps {
  chat_id: string;
  handleActiveSession: (chat_id: string) => void;
}
const PinnedItem = (props: PinnedItemProps) => {
  const { client, sessionsExpanded, activatedSessionId } = useAppStore(
    useShallowEnhance([
      'activatedSessionId',
      'client',
      'sessionsExpanded',
      'changePinnedList',
      'sessionsStatus',
    ])
  );
  const { t } = useTranslation();
  const [info, setInfo] = useState<Session>();
  const { chat_id, handleActiveSession } = props;
  const isGroup = useMemo(
    () => isSessionCategory(chat_id, SessionCategoryPrefix.Group),
    [chat_id]
  );
  const isRobot = useMemo(
    () => info?.user_info?.account_type === AccountType.Robot,
    [info?.user_info?.account_type]
  );
  const hasMention = useMemo(() => !!info?.at_message_list?.length, [info]);
  const active = useMemo(() => activatedSessionId === chat_id, [activatedSessionId, chat_id]);
  const data = useMemo(() => ({ session: info }), [info]);

  const getPinnedListInfo = useCallback(async () => {
    try {
      const _session = (await client?.Api.session.getSessionById(
        chat_id
      )) as unknown as OriginalSession;
      if (_session) {
        const _result = client?.formatSession(_session);
        if (_result) {
          setInfo(_result);
        }
      }
    } catch (_) {
      //The removal of pinned item cannot be found in storage.
      // changePinnedList(chat_id, false, false);
    }
  }, [client, chat_id]);

  useEffect(() => {
    const _session = find(sessionsExpanded, { chat_id });
    if (_session) {
      setInfo(_session);
    }
  }, [sessionsExpanded, chat_id]);
  useEffect(() => {
    if (info) {
      return;
    }
    getPinnedListInfo();
  }, [chat_id, info, getPinnedListInfo]);
  const { unread_count, muted_unread_count } = useMemo(() => {
    if (info) {
      return getSessionUnreadCount(info);
    } else {
      return { unread_count: 0, muted_unread_count: 0 };
    }
  }, [info]);
  return (
    <ContextMenuTrigger data={data} height="full">
      <Stack
        w="64px"
        fontSize="xs"
        borderRadius="12px"
        align="center"
        border="1px solid"
        borderColor="border"
        py="2.5"
        px="1.5"
        gap="1.5"
        boxShadow="0px 2px 4px #0000000A"
        pos="relative"
        bg={active ? 'brand.10' : ''}
        cursor="pointer"
        height="full"
        onClick={() => {
          if (find(sessionsExpanded, { chat_id })) {
            handleActiveSession(chat_id);
          } else {
            client?.Api.session
              .createSingleChat(get(info, 'receiver_id', ''))
              .then((res: Session) => {
                handleActiveSession(res.chat_id);
              });
          }
        }}
      >
        <Center pos="absolute" top="0" right="0" transform="translate(20%,-20%)">
          {!hasMention && get(info, 'last_message') && (
            <UnreadBadge
              count={unread_count}
              mutedCount={muted_unread_count}
              maxCount={99}
              minW="18px"
              h="18px"
              css={{
                '&>p': {
                  lineHeight: '18px',
                  fontSize: 'xs',
                },
              }}
              bg={get(info, 'operateAccess.MUTED') ? 'grey.88' : 'red'}
            />
          )}
          {hasMention && <MentionIcon width="18px" />}
        </Center>
        <Box pos="relative" w="36px" h="36px">
          <Avatar src={get(info, 'avatar')} w="full" h="full" cursor="pointer" />
          <Center pos="absolute" bottom="0" right="0" fontSize="sm">
            {isGroup && <GroupIcon />}
            {isRobot && (
              <Icon w="14px" h="14px">
                <RobotIcon />
              </Icon>
            )}
          </Center>
        </Box>
        <Text truncate fontWeight="medium" w="full" textAlign="center">
          {isGroup
            ? get(info, 'name')
            : get(info, 'user_info.state') === StateEnum.DELETE_ACCOUNT
              ? t('chat.deleteAccountText')
              : get(info, 'user_info.name')}
        </Text>
      </Stack>
    </ContextMenuTrigger>
  );
};
const MemoizedPinnedItem = memo(PinnedItem);
export { MemoizedPinnedItem as PinnedItem };
